import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDateTime } from '../../lib/datetime';
import {
  ArrowLeft,
  Boxes,
  CheckCircle2,
  LayoutTemplate,
  Plus,
  Save,
  Star,
  StarOff,
  Trash2,
  Users,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  type SelectOption,
  useToast,
} from '../../ui';
import { useRooms } from '../../features/rooms/useRooms';
import { useTemplates } from '../../features/templates/useTemplates';
import { useActiveRoom } from '../../features/rooms/RoomContext';
import { validateRoom } from '../../features/rooms/roomModel';
import type { Room, RoomMember } from '../../features/rooms/types';
import { MemberRow } from './MemberRow';
import { usePeopleDirectory } from '../../features/teams/usePeopleDirectory';

/**
 * Room detail / settings — view and edit a room's name, description, members
 * and default template (picked from the Templates feature). Supports marking
 * the room active (so Tasks/Estimation scope to it) and (soft) deletion. Pages
 * call the useRooms / useTemplates hooks and the active-room context only.
 */
export function RoomDetailPage() {
  const params = useParams<{ id?: string }>();
  const id = params.id ? decodeURIComponent(params.id) : undefined;
  const navigate = useNavigate();
  const { toast } = useToast();

  const { getById, update, remove } = useRooms();
  const { groups, loading: templatesLoading } = useTemplates();
  const { activeRoomId, setActiveRoomId, refreshActiveRoom } = useActiveRoom();
  const { people } = usePeopleDirectory();

  const [draft, setDraft] = useState<Room | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Load the requested room.
  useEffect(() => {
    let active = true;
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    void getById(id)
      .then((room) => {
        if (!active) return;
        if (!room || room.deleted) setNotFound(true);
        else setDraft(room);
      })
      .catch(() => {
        if (active) setNotFound(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, getById]);

  const templateOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'No default template' },
      ...groups.map((g) => ({
        value: g.latest.id,
        label: `${g.name} (v${g.latest.version})`,
      })),
    ],
    [groups],
  );

  const patchDraft = useCallback((patch: Partial<Room>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  // ---- Member operations ------------------------------------------------
  const addMember = () =>
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            members: [
              ...prev.members,
              { alias: '', name: '', role: 'member' } satisfies RoomMember,
            ],
          }
        : prev,
    );
  const changeMember = (index: number, patch: Partial<RoomMember>) =>
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            members: prev.members.map((m, i) =>
              i === index ? { ...m, ...patch } : m,
            ),
          }
        : prev,
    );
  const removeMember = (index: number) =>
    setDraft((prev) =>
      prev
        ? { ...prev, members: prev.members.filter((_, i) => i !== index) }
        : prev,
    );

  // ---- Persistence ------------------------------------------------------
  const isActive = !!draft && draft.id === activeRoomId;

  const onSave = async () => {
    if (!draft) return;
    const problems = validateRoom(draft);
    if (problems.length > 0) {
      toast({ tone: 'error', title: 'Cannot save', description: problems[0] });
      return;
    }
    setSaving(true);
    try {
      const saved = await update({
        ...draft,
        name: draft.name.trim(),
        defaultTemplateId: draft.defaultTemplateId || undefined,
      });
      setDraft(saved);
      if (saved.id === activeRoomId) await refreshActiveRoom();
      toast({ tone: 'success', title: 'Room saved', description: saved.name });
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Save failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
    }
  };

  const onToggleActive = () => {
    if (!draft) return;
    if (isActive) {
      setActiveRoomId(null);
      toast({ tone: 'info', title: 'Cleared active room' });
    } else {
      setActiveRoomId(draft.id);
      toast({ tone: 'success', title: 'Active room set', description: draft.name });
    }
  };

  const onConfirmDelete = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await remove(draft.id);
      if (draft.id === activeRoomId) setActiveRoomId(null);
      toast({ tone: 'success', title: 'Room deleted', description: draft.name });
      navigate('/rooms');
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Delete failed',
        description: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  // ---- Render -----------------------------------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading room" />
      </div>
    );
  }

  if (notFound || !draft) {
    return (
      <div>
        <PageHeader
          icon={<Boxes className="h-5 w-5" />}
          title="Room not found"
          actions={
            <Button
              variant="outline"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/rooms')}
            >
              Back to rooms
            </Button>
          }
        />
        <EmptyState
          icon={<Boxes className="h-6 w-6" />}
          title="We couldn't load that room"
          description={id ? `No room exists with id "${id}".` : undefined}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        icon={<Boxes className="h-5 w-5" />}
        title={draft.name || 'Room'}
        description={`Created by ${draft.createdBy || 'unknown'}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate('/rooms')}
            >
              Back
            </Button>
            <Button
              variant={isActive ? 'secondary' : 'outline'}
              leftIcon={
                isActive ? (
                  <StarOff className="h-4 w-4" />
                ) : (
                  <Star className="h-4 w-4" />
                )
              }
              onClick={onToggleActive}
            >
              {isActive ? 'Clear active' : 'Set active'}
            </Button>
            <Button
              variant="danger"
              leftIcon={<Trash2 className="h-4 w-4" />}
              disabled={saving}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              loading={saving}
              onClick={onSave}
            >
              Save
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Room settings</CardTitle>
              {isActive && (
                <Badge tone="success" dot>
                  active
                </Badge>
              )}
            </CardHeader>
            <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Name"
                value={draft.name}
                placeholder="Payments Platform Q3"
                onChange={(e) => patchDraft({ name: e.target.value })}
              />
              <Select
                label="Default template"
                value={draft.defaultTemplateId ?? ''}
                options={templateOptions}
                disabled={templatesLoading}
                onChange={(e) =>
                  patchDraft({ defaultTemplateId: e.target.value || undefined })
                }
              />
              <Textarea
                label="Description"
                className="sm:col-span-2"
                value={draft.description ?? ''}
                placeholder="What work does this room track?"
                onChange={(e) => patchDraft({ description: e.target.value })}
              />
            </CardBody>
          </Card>

          {/* Members */}
          <Card>
            <CardHeader>
              <CardTitle>
                <span className="inline-flex items-center gap-2">
                  <Users className="h-4 w-4 text-zinc-500" />
                  Members ({draft.members.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardBody className="space-y-3">
              {draft.members.length === 0 ? (
                <EmptyState
                  icon={<Users className="h-6 w-6" />}
                  title="No members"
                  description="Add members by alias. Seed roles from owner to viewer."
                />
              ) : (
                draft.members.map((member, i) => (
                  <MemberRow
                    key={`${member.alias}-${i}`}
                    member={member}
                    createdBy={draft.createdBy}
                    onChange={(patch) => changeMember(i, patch)}
                    onRemove={() => removeMember(i)}
                    people={people}
                  />
                ))
              )}
              <Button
                variant="secondary"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={addMember}
              >
                Add member
              </Button>
            </CardBody>
          </Card>
        </div>

        {/* Summary side panel */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardBody className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Members</span>
                <Badge tone="neutral">{draft.members.length}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Default template</span>
                {draft.defaultTemplateId ? (
                  <Badge tone="info">
                    <LayoutTemplate className="mr-1 h-3 w-3" />
                    set
                  </Badge>
                ) : (
                  <Badge tone="neutral">none</Badge>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-zinc-500">Active room</span>
                {isActive ? (
                  <Badge tone="success">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    yes
                  </Badge>
                ) : (
                  <Badge tone="neutral">no</Badge>
                )}
              </div>
              <div className="border-t border-surface-border pt-3 text-xs text-zinc-500">
                Created {formatDateTime(draft.createdAt)}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <Modal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this room?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={saving} onClick={onConfirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-zinc-300">
          This will remove{' '}
          <span className="font-medium text-zinc-100">{draft.name}</span> and
          hide it from the rooms list. Tasks and estimates scoped to it are not
          deleted.
        </p>
      </Modal>
    </div>
  );
}

export default RoomDetailPage;
